import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, DirectionsService, DirectionsRenderer } from '@react-google-maps/api';
import randomColor from 'randomcolor';
import markerDot from './assets/marker-dot-red.png';
import markerDotGreen from './assets/marker-dot-green.png';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import DELIVERY_STATUS from '../../components/constants/deliveryStatus';

const containerStyle = {
  width: '100%',
  height: '600px',
};

const center = {
  lat: 58.6942,
  lng: 24.9077,
};

const MEMBO_WAREHOUSE_COORDINATES = {
  lat: 59.421957,
  lon: 24.798331,
};

const RoutesMap = ({ routes, routePlan }) => {
  // eslint-disable-next-line no-unused-vars
  const [map, setMap] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [directions, setDirections] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);

  const [routesWithColors, setRoutesWithColors] = useState([]);
  const routesWithColorsRef = useRef(routesWithColors); // need to use useRef because timeout for directions fetch: https://medium.com/programming-essentials/how-to-access-the-state-in-settimeout-inside-a-react-function-component-39a9f031c76f
  routesWithColorsRef.current = routesWithColors;

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  useEffect(() => {
    setRoutesWithColors(routes.map((route) => ({ route, color: randomColor() })));
    setDirections([]);
  }, [routes]);

  useEffect(() => {
    if (selectedRoute && directionsService) {
      setTimeout(() => {
        fetchDirectionsForRoutes(directionsService);
      }, 500);
    }
  }, [selectedRoute]);

  const fetchDirectionsForRoutes = (directionsService) => {
    const routeWithColor = routesWithColors.find((withColor) => withColor.route.id === selectedRoute?.id);
    if (!routeWithColor) {
      return;
    }

    const wayPoints = routeWithColor.route.deliveries.map((delivery) => ({
      location: { lat: Number(delivery.coordinates.lat), lng: Number(delivery.coordinates.lon) },
      stopover: true,
    }));

    const lastDelivery = routeWithColor.route.deliveries[routeWithColor.route.deliveries.length - 1];

    const getDirectionsForRoute = (route, callBack) => {
      directionsService.route(
        {
          origin: {
            lat: Number(
              route.startDepot?.coordinates.lat ||
                routePlan.startDepot?.address.coordinates.lat ||
                MEMBO_WAREHOUSE_COORDINATES.lat
            ),
            lng: Number(
              route.startDepot?.coordinates.lon ||
                routePlan.startDepot?.address.coordinates.lon ||
                MEMBO_WAREHOUSE_COORDINATES.lon
            ),
          },
          destination: { lat: Number(lastDelivery.coordinates.lat), lng: Number(lastDelivery.coordinates.lon) },
          waypoints: wayPoints,
          travelMode: 'DRIVING',
        },
        callBack
      );
    };

    getDirectionsForRoute(routeWithColor.route, (response, status) => {
      if (status === 'OK') {
        setDirections([{ routeId: routeWithColor.route.id, response, color: routeWithColor.color }]);
      }
    });
  };

  const onMarkerClick = (delivery) => {
    const deliveryRoute = routes.find((route) => route.deliveries.some((d) => d.id === delivery.id));
    if (deliveryRoute) {
      setSelectedRoute(deliveryRoute);
    } else {
      setSelectedDelivery(delivery);
    }
  };

  let markers = routePlan.deliveries.map((delivery) => {
    return (
      <Marker
        key={delivery.id}
        icon={delivery.status === DELIVERY_STATUS.finished.name ? markerDotGreen : markerDot}
        onLoad={onLoad}
        onClick={() => onMarkerClick(delivery)}
        label={
          selectedDelivery?.id === delivery.id || selectedRoute?.deliveries.some((d) => d.id === delivery.id)
            ? {
                text: delivery.name,
                color: '#000000',
                fontSize: '12px',
              }
            : null
        }
        position={{
          lat: Number(delivery.coordinates.lat),
          lng: Number(delivery.coordinates.lon),
        }}
      />
    );
  });

  return (
    <div>
      <div className="m-b-3">
        {routesWithColors.map(({ route, color }, index) => {
          const isSelected = selectedRoute?.id === route.id;
          const badgeClasses = classNames({
            'badge m-r-1 text-white': true,
            'badge-primary': isSelected,
            'badge-secondary': !isSelected,
          });

          return (
            <span
              key={index}
              style={!isSelected ? {} : { background: `${color}` }}
              className={badgeClasses}
              onClick={() => (isSelected ? setSelectedRoute(null) : setSelectedRoute(route))}
            >
              <FormattedMessage id={'admin.route.planning.edit.map.badge'} values={{ routeId: route.id }} />
            </span>
          );
        })}
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={7.8}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          mapId: '4797b96dc63299d7',
        }}
      >
        <>
          {markers}

          <DirectionsService
            options={{
              origin: { lat: Number(MEMBO_WAREHOUSE_COORDINATES.lat), lng: Number(MEMBO_WAREHOUSE_COORDINATES.lon) },
              destination: {
                lat: Number(MEMBO_WAREHOUSE_COORDINATES.lat),
                lng: Number(MEMBO_WAREHOUSE_COORDINATES.lon),
              },
              travelMode: 'DRIVING',
            }}
            callback={() => {}}
            onLoad={(newDirectionsService) => {
              fetchDirectionsForRoutes(newDirectionsService);
              setDirectionsService(newDirectionsService);
            }}
          />

          {directions
            ? directions.map(({ response, routeId, color }) => (
                <DirectionsRenderer
                  key={routeId}
                  options={{
                    directions: response,
                    polylineOptions: {
                      strokeColor: color,
                    },
                    suppressMarkers: true,
                  }}
                />
              ))
            : ''}
        </>
      </GoogleMap>
    </div>
  );
};

RoutesMap.propTypes = {};

RoutesMap.defaultProps = {};

export default RoutesMap;
